/**
 * Labels, defaults and input config for the bundle pricing calculator.
 *
 * English-only by design: the tool lives under /tools, which ships to the
 * English locales only (see EN_ONLY_SLUGS). Currency is the one locale-varying
 * piece and is resolved at render time from the request locale, not hardcoded
 * here.
 */

export const STRINGS = {
  toolTitle: "Your bundle",
  itemName: "Item name",
  price: "Price",
  cost: "Cost",
  addItem: "Add item",
  removeItem: "Remove item",
  // Mode toggle
  modeLabel: "What do you want to work out?",
  modeMaxDiscount: "Max discount",
  modeMargin: "Margin at a discount",
  targetMargin: "Target blended margin",
  discount: "Bundle discount",
  // Support-row labels
  listPrice: "Bundle list price",
  bundleCost: "Bundle cost",
  marginBefore: "Blended margin before discount",
  discountedPrice: "Bundle price after discount",
  marginPerBundle: "Margin per bundle",
  disclaimer:
    "Estimates only, based on the numbers you enter. Blended margin is measured on the discounted bundle price and assumes every item sells together; it doesn't account for fixed costs, returns or the effect of price on demand.",
} as const;

/** Input ranges and step sizes. Inputs are clamped to these bounds. */
export const RANGES = {
  // Both percentage sliders stop at 99%: a 100% target needs zero cost, and a
  // 100% discount gives the bundle away — neither is a real pricing decision,
  // and stopping short keeps the slider in the range founders actually use.
  targetMargin: { min: 0, max: 99, step: 1 },
  discount: { min: 0, max: 99, step: 1 },
} as const;

/** Number of item rows the tool allows. A bundle needs at least one item; the
 * ceiling keeps the layout and the maths manageable. */
export const ITEM_LIMITS = { min: 1, max: 6 } as const;

/**
 * Neutral, round placeholder values the user is meant to change. Deliberately
 * not derived from any Wayflyer merchant data. Two items at 30/12 and 20/8 give
 * a clean 60% natural blended margin, so on load the max-discount headline
 * lands on a sensible ~33% at the 40% target without looking inert.
 */
export const DEFAULTS = {
  items: [
    { name: "", price: "30", cost: "12" },
    { name: "", price: "20", cost: "8" },
  ],
  targetMarginPct: 40,
  discountPct: 25,
} as const;

export interface FaqQuestion {
  question: string;
  answer: string;
}

export const FAQ_TITLE = "Bundle pricing FAQ";

export const FAQ_QUESTIONS: FaqQuestion[] = [
  {
    question: "What is bundle pricing?",
    answer:
      "Bundle pricing means selling two or more products together for one combined price, usually at a discount compared to buying each item separately. It works for you as a seller if the discount still leaves a healthy margin across the whole bundle, which is what this calculator checks before you launch the offer.",
  },
  {
    question: "How much discount should I offer on a bundle?",
    answer:
      "A common starting point is 10 to 30% off the combined list price, deep enough to feel like a real deal without training customers to expect it every time. But the right number for your bundle depends on your margins, which is exactly what this tool works out: set a target blended margin and it finds the deepest discount that still holds it.",
  },
  {
    question: "What is blended margin, and why does this tool use it?",
    answer:
      "Blended margin is the overall margin across every item in the bundle combined, rather than looking at each item's margin on its own. A bundle can mix high and low margin items, so grading the bundle as a whole (blended) shows whether the combined offer is healthy even if one item barely breaks even.",
  },
  {
    question: "How does the max discount mode work?",
    answer:
      "Set a target blended margin and this tool works backwards to find the deepest discount your bundle can take while still holding that margin, based on the price and cost you enter for every item. Push the discount any further than that and your blended margin drops below the target you set.",
  },
  {
    question: "What's the difference between the two modes?",
    answer:
      "**Max discount** starts from a target margin and tells you the biggest discount you can offer. **Margin at a discount** works the other way: pick a discount you're already planning to run, and it shows the blended margin that discount actually leaves you.",
  },
  {
    question: "Does this account for fixed costs, returns or demand effects?",
    answer:
      "No. The margin shown is measured on the discounted bundle price and assumes every item in the bundle sells together; it doesn't account for fixed costs, returns, or how a lower price might change how many bundles you sell. Treat the result as your pricing floor, not a full profit forecast.",
  },
  {
    question: "Where can I get help with the rest of my Black Friday offer?",
    answer:
      "Check the maths on single-item discounts with the [discount break-even calculator](/tools/discount-break-even), or work out how much stock to order with the [order quantity calculator](/tools/order-quantity-calculator). See every tool in the [full Black Friday toolkit](/tools).",
  },
];
